import { useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Paperclip, TrendingUp, BarChart2, LineChart, PieChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Sample conversation data for growth channel
const conversationData = [
  {
    id: 1,
    sender: "GrowthAgent",
    avatarSrc: "/crew-agents/handsoff.jpg",
    message: "I've completed the Q3 growth strategy analysis. Here are our current performance metrics compared to targets:",
    timestamp: "Today at 8:30 AM",
    isUser: false,
    attachment: {
      type: "chart",
      title: "Q3 Performance Metrics",
      description: "Conversion rates have increased 18% MoM, but user acquisition is 12% below target."
    }
  },
  {
    id: 2,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "I've analyzed competitor growth strategies for Q3. Three key competitors have shifted focus to mobile-first acquisition, resulting in 25-30% higher conversion rates for mobile users.",
    timestamp: "Today at 8:42 AM",
    isUser: false
  },
  {
    id: 3,
    sender: "AnalyticsBot",
    avatarSrc: "/crew-agents/webanalytics.png",
    message: "Adding to GrowthAgent's report - I've identified that our highest performing acquisition channels have changed significantly this quarter. Social media now delivers 37% of new users (up from 24%) while direct search has decreased to 28% (down from 41%).",
    timestamp: "Today at 8:55 AM",
    isUser: false
  },
  {
    id: 4,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Great insights, team. Based on this data, I think we should reconsider our channel allocation for Q4. @GrowthAgent, can you propose a revised budget allocation that increases social media investment by 20% and focuses more on mobile optimization?",
    timestamp: "Today at 9:15 AM",
    isUser: true
  },
  {
    id: 5,
    sender: "GrowthAgent",
    avatarSrc: "/crew-agents/handsoff.jpg",
    message: "I'll work on that budget reallocation. Would you also like me to include a projected impact analysis for the changes?",
    timestamp: "Today at 9:18 AM",
    isUser: false
  },
  {
    id: 6,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Yes, please include impact projections for both the conservative and aggressive scenarios.",
    timestamp: "Today at 9:20 AM",
    isUser: true
  },
  {
    id: 7,
    sender: "GrowthAgent",
    avatarSrc: "/crew-agents/handsoff.jpg",
    message: "I've completed the Q4 budget reallocation with projected impact analysis. Key changes:\n\n1. Social media budget increased by 20% (from $35K to $42K)\n2. Mobile optimization investment increased by 30% (from $20K to $26K)\n3. Reduced SEM budget by 15% (from $30K to $25.5K)\n\nProjected impact (conservative scenario):\n- 22% increase in mobile conversions\n- 15% overall increase in conversion rate\n- 8% increase in user acquisition\n\nProjected impact (aggressive scenario):\n- 35% increase in mobile conversions\n- 25% overall increase in conversion rate\n- 18% increase in user acquisition\n\nWould you like me to present this to the marketing team?",
    timestamp: "Today at 10:45 AM",
    isUser: false,
    attachment: {
      type: "report",
      title: "Q4 Budget Reallocation",
      description: "Complete budget reallocation with impact projections"
    }
  }
];

// Team members for this channel
const teamMembers = [
  {
    name: "GrowthAgent",
    role: "Growth Specialist",
    avatarSrc: "/crew-agents/handsoff.jpg",
    status: "online"
  },
  {
    name: "CompetitorBot",
    role: "Competitor Intelligence",
    avatarSrc: "/crew-agents/competitors.png", 
    status: "online"
  },
  {
    name: "AnalyticsBot",
    role: "Analytics Specialist",
    avatarSrc: "/crew-agents/webanalytics.png",
    status: "away"
  },
  {
    name: "You",
    role: "Marketing Director",
    avatarSrc: "/crew-agents/page-semantic.png",
    status: "online"
  }
];

// Growth reports
const growthReports = [
  {
    id: 201,
    title: "Q3 Performance Analysis",
    description: "Complete analysis of Q3 growth metrics and KPIs",
    date: "Today",
    creator: "GrowthAgent",
    icon: <BarChart2 className="h-4 w-4 text-blue-500" />
  },
  {
    id: 202,
    title: "Q4 Budget Reallocation",
    description: "Updated budget allocation with projected impact",
    date: "Today",
    creator: "GrowthAgent",
    icon: <PieChart className="h-4 w-4 text-green-500" />
  },
  {
    id: 203,
    title: "Competitor Strategy Analysis",
    description: "Detailed analysis of competitor growth tactics",
    date: "Yesterday",
    creator: "CompetitorBot",
    icon: <LineChart className="h-4 w-4 text-purple-500" />
  },
  {
    id: 204,
    title: "Channel Performance Report",
    description: "Analysis of acquisition channel effectiveness",
    date: "2 days ago",
    creator: "AnalyticsBot",
    icon: <BarChart2 className="h-4 w-4 text-blue-500" />
  }
];

// Growth KPIs
const growthKPIs = [
  {
    metric: "User Acquisition",
    value: "12,458",
    change: "+8.3%",
    trend: "up"
  },
  {
    metric: "Conversion Rate",
    value: "3.8%",
    change: "+1.2%",
    trend: "up"
  },
  {
    metric: "CAC",
    value: "$22.15",
    change: "-5.3%",
    trend: "down"
  },
  {
    metric: "MRR",
    value: "$248K",
    change: "+18.7%",
    trend: "up"
  }
];

const ChatMessage = ({ message }: { message: any }) => {
  const isUser = message.isUser;
  
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
      
      <div className={`max-w-[70%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {message.sender}
          </span>
          <span className={`text-xs ml-4 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {message.timestamp}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.message}
        </p>
        
        {message.attachment && (
          <div className={`mt-2 p-2 rounded border ${
            isUser ? 'border-white/20 bg-white/10' : 'border-gray-200 bg-background'
          }`}>
            <div className="flex items-center gap-2">
              {message.attachment.type === 'chart' ? (
                <BarChart2 className="h-4 w-4 text-blue-500" />
              ) : (
                <PieChart className="h-4 w-4 text-green-500" />
              )}
              <span className="font-medium text-xs">{message.attachment.title}</span>
            </div>
            <p className={`text-xs mt-1 ${isUser ? 'text-white/70' : 'text-muted-foreground'}`}>
              {message.attachment.description}
            </p>
          </div>
        )}
      </div>
      
      {isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
    </div>
  );
};

const ReportItem = ({ report }: { report: any }) => {
  return (
    <div className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/80 cursor-pointer">
      <div className="h-8 w-8 rounded bg-muted/80 flex items-center justify-center">
        {report.icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm truncate">{report.title}</h4>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground truncate max-w-[150px]">{report.description}</p>
          <span className="text-xs text-muted-foreground">{report.date}</span>
        </div>
      </div>
    </div>
  );
};

const KPIItem = ({ kpi }: { kpi: any }) => {
  return (
    <div className="bg-background p-3 rounded-md border">
      <div className="flex justify-between items-center mb-1">
        <p className="text-xs text-muted-foreground">{kpi.metric}</p>
        <Badge 
          variant="outline" 
          className={`text-xs ${
            kpi.trend === 'up' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {kpi.change}
        </Badge>
      </div>
      <p className="text-xl font-bold">{kpi.value}</p>
    </div>
  );
};

const GrowthChannelPage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(conversationData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    document.title = "Growth Channel | PageMind Admin";
    
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim()) {
      // Add user message
      const newUserMessage = {
        id: conversation.length + 1,
        sender: "You",
        avatarSrc: "/crew-agents/page-semantic.png",
        message,
        timestamp: "Just now",
        isUser: true
      };
      
      setConversation([...conversation, newUserMessage]);
      setMessage("");
      
      // Simulate response after a delay
      setTimeout(() => {
        const botResponse = {
          id: conversation.length + 2,
          sender: "GrowthAgent",
          avatarSrc: "/crew-agents/handsoff.jpg",
          message: "Yes, I'll prepare a presentation for the marketing team meeting. Would you like me to create slides highlighting the key changes and projected outcomes?",
          timestamp: "Just now",
          isUser: false
        };
        
        setConversation(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/insights/channels')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center">
            <div className="flex items-center gap-1 bg-primary/10 rounded-full px-3 py-1.5">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <div className="font-bold flex items-center gap-2">
                  Growth
                  <Badge variant="outline" className="text-xs bg-primary/5">Team Channel</Badge>
                  <Badge className="bg-green-500">7 Unread</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <Button variant="outline" size="sm">
            Channel Settings
          </Button>
        </div>
      </div>
      
      {/* Main content - split into chat and sidebar */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-6">
            {growthKPIs.map((kpi, index) => (
              <KPIItem key={index} kpi={kpi} />
            ))}
          </div>
          
          {conversation.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 hidden lg:block">
          <Tabs defaultValue="reports">
            <TabsList className="w-full">
              <TabsTrigger value="reports" className="flex-1">Reports</TabsTrigger>
              <TabsTrigger value="members" className="flex-1">Members</TabsTrigger>
            </TabsList>
            
            <TabsContent value="reports" className="mt-4">
              <div className="space-y-1">
                {growthReports.map(report => (
                  <ReportItem key={report.id} report={report} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="members" className="mt-4">
              <div className="space-y-3">
                {teamMembers.map((member, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="relative">
                      <Avatar>
                        <AvatarImage src={member.avatarSrc} alt={member.name} />
                        <AvatarFallback>{member.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background ${
                        member.status === 'online' ? 'bg-green-500' : 
                        member.status === 'away' ? 'bg-yellow-500' : 'bg-gray-400'
                      }`} />
                    </div>
                    <div>
                      <div className="text-sm font-medium">{member.name}</div>
                      <div className="text-xs text-muted-foreground">{member.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Message #growth..."
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!message.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default GrowthChannelPage;